/**
 * useOfflineRecorder — offline gating, session lifecycle, and control flow
 * for the OfflineRecorder UI component (US1 / Phase 2).
 *
 * Gating (FR-003): canRecordOffline is false until the cached-identity read
 * confirms a prior online login has been cached. Null identity = never logged
 * in online = cannot record offline. The read itself — including what a failed
 * one means — belongs to `useCanRecordOffline`, which this hook consumes.
 *
 * Capture status and the active recording id are SHARED app-wide via Jotai
 * atoms, not held per-hook-instance. This hook is mounted independently by the
 * record button, the session keyboard shortcuts, and the start-prompt overlay;
 * with per-instance state the mount that starts a capture diverged from the one
 * that renders the button or handles stop/pause, leaving the latter on a `null`
 * id (its controls silently no-op) and an `idle` status (the button never
 * leaves "00:00"). Sourcing both from the atoms — which the
 * `offline_recording:capture_started` / `pending_sync` events also drive — keeps
 * every mount reflecting the one live capture.
 */
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';

import {
  offlineActiveRecordingIdAtom,
  offlineCapturePausedAtom,
  offlineLocalSessionsAtom,
} from '@/atoms/offline-recording/sync';
import { getTauriLastUsedInputDevice } from '@/atoms/persistedPreferencesAtoms';
import { useCanRecordOffline } from '@/hooks/desktop/useCanRecordOffline';
import { getAvailableDevices } from '@/tauri/audio-recorder/devices';
import {
  releaseIdleStreamForCapture,
  restoreIdleStreamAfterCapture,
} from '@/tauri/audio-recorder/idle-stream-handover';
import { SYSTEM_DEFAULT_DEVICE_MARKER } from '@/tauri/constants';
import type { OfflineSyncStatus } from '@/tauri/offline-recording/offline-recording';
import {
  offlineRecordingBridge,
  type CreateOfflineRecordingArgs,
  type CreateOfflineRecordingResult,
  type OfflineCaptureIntent,
} from '@/tauri/offline-recording/recording';
import { logger } from '@/utils/logger/logger';

type RecordingStatus = OfflineSyncStatus | 'idle';

/**
 * A control that has nothing to act on REJECTS rather than resolving.
 *
 * Each of the control callbacks below acts on the shared active-recording id,
 * and each of them used to `return` when there was none. A resolved promise is
 * read by every caller as "the operation succeeded", so the caller then ran its
 * success path: it froze or stopped the shared timer, settled the UI, and filed
 * a completed stop in telemetry — while nothing had been asked of Rust and the
 * capture, if any, was still running. A control that cannot act and says
 * nothing is the worst of the three outcomes, because it is indistinguishable
 * from the one that worked.
 *
 * Rejecting costs no audio: with no active id there is no capture for this hook
 * to end, so there is nothing here to preserve. The recovery belongs to the
 * caller, which can now see the failure.
 */
const NO_ACTIVE_CAPTURE = 'no active offline capture to act on';
const NO_STAGED_SESSION = 'no offline session staged to start';

export interface UseOfflineRecorderReturn {
  /**
   * Create — or resolve — the consult the next {@link start} records into.
   *
   * Resolves the OUTCOME rather than a bare id, because the id alone no longer
   * says what the consult holds: a create naming a `serverSessionId` this
   * device already has a local consult for lands on that consult. The caller
   * must read `hasCapturedAudio` off the result to choose an intent, exactly as
   * the {@link useExistingSession} path reads it off the queue.
   */
  create: (args: CreateOfflineRecordingArgs) => Promise<CreateOfflineRecordingResult>;
  /**
   * Adopt an already-created local session (created in an earlier mount, or in
   * an earlier RUN of the app) so a subsequent `start()` records into it instead
   * of `create()`-ing a new one.
   */
  useExistingSession: (localSessionId: string) => void;
  /**
   * Begin capture on the staged session.
   *
   * `intent` tells Rust whether this consult is expected to be empty, and the
   * question is always the same one: **does the staged consult already hold a
   * take?** `'resume'` when it does and the clinician means to add to it,
   * `'start_fresh'` when it does not — which keeps Rust's collision check armed
   * on the first take.
   *
   * Where a caller reads that fact depends only on how it staged the consult.
   * A {@link useExistingSession} id is answered from the offline queue; a
   * {@link create} id is answered from that call's own
   * `hasCapturedAudio`. **`create()` is not a guarantee of an empty consult** —
   * one naming a `serverSessionId` the device already holds a local consult for
   * resolves to that consult, takes and all, which is why it reports rather
   * than just returning an id.
   *
   * Deliberately a parameter rather than something this hook infers: only the
   * caller knows whether the consult it named already holds captured audio, and
   * that fact survives the app being quit between halves of an encounter where
   * the elapsed timer and the rendered button label do not.
   *
   * @throws {@link NO_STAGED_SESSION} when no local session has been staged by
   * {@link create} or {@link useExistingSession}. This is the staged-session
   * rejector and not the live-capture one: `start` reads
   * `pendingSessionIdRef`, while pause/resume/stop read the shared
   * active-recording id. A control that cannot act must not resolve.
   */
  start: (intent: OfflineCaptureIntent) => Promise<void>;
  /** @throws when no capture is live — see the `NO_ACTIVE_CAPTURE` note. */
  pause: () => Promise<void>;
  /** @throws when no capture is live — see the `NO_ACTIVE_CAPTURE` note. */
  resume: () => Promise<void>;
  /** @throws when no capture is live — see the `NO_ACTIVE_CAPTURE` note. */
  stop: () => Promise<void>;
  status: RecordingStatus;
  isCapturePaused: boolean;
  canRecordOffline: boolean;
}

/**
 * The persisted microphone selection, clamped to a device capture can actually
 * open. `undefined` means "pass nothing", which is what makes Rust fall through
 * to the create-time `offline_sessions.microphone` column and then to the OS
 * default — exactly the behaviour that shipped before a selection was passed at
 * all.
 *
 * The clamp is why this is not just a read. `tauriLastUsedInputDevice` is
 * `atomWithStorage`, rehydrated from disk at boot, so it names last session's
 * device before anything has confirmed that device is still present — a headset
 * left at home, a different dock at the clinic. Rust resolves `InputDevice(name)`
 * by exact match against `host.input_devices()` and rejects with `NoInputDevice`
 * otherwise, and on the `useRecordingButton` create branch (which writes no
 * `microphone` column) that rejection is only logged. Unclamped, this would turn
 * "records on the wrong microphone, silently" into "does not record at all,
 * silently", which for a consult is the worse of the two.
 *
 * This is the half of the online start path the read alone did not match:
 * `useMutateStartTauriRecording` re-enumerates ("// Check device validity") and
 * only then reads the reconciled Redux selection, which can never name a device
 * that was not just seen. Redux is not persisted; the atom is.
 *
 * Enumeration is the same `cpal::default_host().input_devices()` walk capture
 * itself matches against, so this is the precise predicate rather than an
 * approximation of it — and it is cheaper than the online equivalent, which
 * additionally does the Windows process-audio and icon pass.
 *
 * Fails OPEN on purpose: enumeration is fallible and throws outright off Tauri,
 * and a start that cannot be checked must still record. Never make the record
 * button do nothing.
 */
async function presentMicrophoneSelection(): Promise<string | undefined> {
  const selected = getTauriLastUsedInputDevice();
  // Nothing selected: no enumeration for a start that was always falling
  // through to the column. The sentinel is a deliberate "whatever the OS calls
  // default", not a device name, so it is never in the list and must not be
  // clamped away.
  if (!selected?.trim() || selected === SYSTEM_DEFAULT_DEVICE_MARKER) {
    return selected;
  }

  try {
    const { input_devices: inputDevices } = await getAvailableDevices();
    if (inputDevices.some((device) => device.name === selected)) return selected;
    logger.warn(
      '[offline-capture] persisted microphone is not present; falling back to the create-time selection'
    );
  } catch (error) {
    logger.warn('[offline-capture] could not enumerate input devices', { error });
  }
  return undefined;
}

export function useOfflineRecorder(): UseOfflineRecorderReturn {
  // One identity read, one fail-closed policy, one report — see
  // {@link useCanRecordOffline}. This hook previously ran its own copy of the
  // read whose `.then` had no rejection branch, so a denied read left it on the
  // initial `false` while never reporting why, and escaped as an unhandled
  // promise rejection. `undefined` (still pending) is not yet a yes, so it maps
  // to `false` here exactly as the local `useState(false)` did.
  const canRecordOffline = useCanRecordOffline() === true;

  // The single, app-wide live capture. Read from the shared atoms so every mount
  // of this hook resolves the SAME recording (see file header).
  const activeRecordingId = useAtomValue(offlineActiveRecordingIdAtom);
  const setActiveRecordingId = useSetAtom(offlineActiveRecordingIdAtom);
  const capturePaused = useAtomValue(offlineCapturePausedAtom);
  const setCapturePaused = useSetAtom(offlineCapturePausedAtom);
  const setOfflineLocalSessions = useSetAtom(offlineLocalSessionsAtom);

  // Stale-closure-safe mirror of the active id for the control callbacks: they
  // must act on whatever is live at click time, regardless of which mount set it
  // (or whether the `capture_started` event set it). Keeping the callbacks out of
  // `activeRecordingId`'s dependency array also stops them — and the returned
  // object — churning on every capture transition.
  const activeRecordingIdRef = useRef(activeRecordingId);
  useEffect(() => {
    activeRecordingIdRef.current = activeRecordingId;
  }, [activeRecordingId]);

  // Pre-start staging slot: the id chosen by create()/useExistingSession() before
  // start() promotes it to the shared atom. Per-instance is correct here — create
  // and the start() that consumes it always run on the same mount, in the same
  // proceedWithPlay tick.
  const pendingSessionIdRef = useRef<string | null>(null);

  // Derived from the shared atoms. `status` is 'recording' whenever a capture is
  // live; pause is a separate, capture-scoped flag (masked to false when idle so
  // a stale paused atom can never read as paused between captures).
  const status: RecordingStatus = activeRecordingId !== null ? 'recording' : 'idle';
  const isCapturePaused = activeRecordingId !== null && capturePaused;

  const create = useCallback(
    async (args: CreateOfflineRecordingArgs): Promise<CreateOfflineRecordingResult> => {
      const result = await offlineRecordingBridge.create(args);
      const id = result.localSessionId;
      const createdAt = new Date().toISOString();
      setOfflineLocalSessions((prev) => {
        const nextLocalSession = {
          localSessionId: id,
          serverSessionId: args.serverSessionId ?? null,
          createdAt,
          updatedAt: createdAt,
        };
        return prev.some((session) => session.localSessionId === id)
          ? prev.map((session) =>
              session.localSessionId === id ? nextLocalSession : session
            )
          : [nextLocalSession, ...prev];
      });
      pendingSessionIdRef.current = id;
      return result;
    },
    [setOfflineLocalSessions]
  );

  const useExistingSession = useCallback((localSessionId: string): void => {
    pendingSessionIdRef.current = localSessionId;
  }, []);

  const start = useCallback(
    async (intent: OfflineCaptureIntent): Promise<void> => {
      const id = pendingSessionIdRef.current;
      if (!id) throw new Error(NO_STAGED_SESSION);
      // Before Rust opens the device, as the online start path does. See
      // `idle-stream-handover` for what the handover owes the wav bars.
      await releaseIdleStreamForCapture(id);
      // Read at use, not at create: the session row's `microphone` column is
      // written once, and a selection made afterwards would never reach the
      // device. Sourced from the persisted atom rather than the store so it
      // survives a cold offline boot — which is also why it is clamped here
      // rather than passed straight through (see `presentMicrophoneSelection`).
      // The clamp belongs at this funnel and not in the callers, so a third
      // caller added later cannot forget it.
      const microphone = await presentMicrophoneSelection();
      try {
        await offlineRecordingBridge.start(id, intent, microphone);
      } catch (error) {
        // A capture that never started emits no `capture_ended`, so this is the
        // only place that can hand the microphone back on this path.
        restoreIdleStreamAfterCapture(id);
        throw error;
      }
      setActiveRecordingId(id);
      setCapturePaused(false);
    },
    [setActiveRecordingId, setCapturePaused]
  );

  const pause = useCallback(async (): Promise<void> => {
    const id = activeRecordingIdRef.current;
    if (!id) throw new Error(NO_ACTIVE_CAPTURE);
    await offlineRecordingBridge.pause(id);
    setCapturePaused(true);
  }, [setCapturePaused]);

  const resume = useCallback(async (): Promise<void> => {
    const id = activeRecordingIdRef.current;
    if (!id) throw new Error(NO_ACTIVE_CAPTURE);
    await offlineRecordingBridge.resume(id);
    setCapturePaused(false);
  }, [setCapturePaused]);

  const stop = useCallback(async (): Promise<void> => {
    const id = activeRecordingIdRef.current;
    if (!id) throw new Error(NO_ACTIVE_CAPTURE);
    // Deliberately not cleared on rejection — Rust owns this transition. It
    // emits `offline_recording:capture_ended` the moment the capture leaves its
    // active slot, and useOfflineRecordingEvents clears the shared atoms from
    // that event. So a stop that fails *after* the take still resets the UI (the
    // event has already fired), while one that fails *before* it — no cached
    // identity after a logout, or an id that doesn't match the live capture —
    // leaves the atoms alone, because that capture is still running and must
    // stay controllable rather than stranding itself behind an idle UI.
    await offlineRecordingBridge.stop(id);
    pendingSessionIdRef.current = null;
    setActiveRecordingId(null);
    setCapturePaused(false);
  }, [setActiveRecordingId, setCapturePaused]);

  return {
    create,
    useExistingSession,
    start,
    pause,
    resume,
    stop,
    status,
    isCapturePaused,
    canRecordOffline,
  };
}
